import { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Plus, Pencil, Power, Loader2, MoreHorizontal, Hash, FileText, Link2, FlaskConical, CheckCircle2, AlertTriangle, XCircle, UserCog } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineLoader } from "@/components/common/Loader";
import { PageHeader } from "@/components/layout/PageHeader";
import { apiErrorText } from "@/lib/errorText";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

const emptyForm = { name_ar: "", name_en: "", code: "", pos_branch_id: "", finance_only: false, hr_branch_id: "", ops_manager_user_id: "" };
// HR keys its branches by UUID; the server checks the same format and uniqueness.
const HR_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Radix Select has no empty value: «no own ops manager» is this sentinel.
const NO_OPS_MANAGER = "__none__";

export default function BranchesPage() {
    const { t } = useI18n();
    const { isSuperAdmin, isSystemAdmin } = useAuth();
    const [branches, setBranches] = useState([]);
    // The branch's own operations manager «مدير العمليات للفرع» (2026-11-01). GET /branches never
    // carries it: it comes from the two system-admin endpoints. {branchId: {user_id, user_name}}
    const [opsManagers, setOpsManagers] = useState({});
    const [opsCandidates, setOpsCandidates] = useState([]);   // [{id, name, email}] the server would accept
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [hrTests, setHrTests] = useState({});   // { [branchId]: {loading, tone, text, hr} }
    // POS id the form held before finance-only was switched on — switching it off puts it back.
    const heldPos = useRef("");
    const [clearing, setClearing] = useState(null);       // links a save would remove -> confirm first
    const [deactivating, setDeactivating] = useState(null);  // branch awaiting typed-name confirm
    const [typedName, setTypedName] = useState("");

    const testHr = async (b) => {
        setHrTests((prev) => ({ ...prev, [b.id]: { loading: true } }));
        try {
            const r = await api.get("/qc-inspection/employees", { params: { branch_id: b.id } });
            const d = r.data || {};
            let res;
            if (d.available) {
                const n = (d.employees || []).length;
                res = n > 0
                    ? { tone: "success", text: `${n} ${t("branch_hr_test_employees")}`, hr: b.hr_branch_id }
                    : { tone: "warn", text: t("branch_hr_test_zero") };
            } else if (d.reason === "hr_unmapped") {
                res = { tone: "warn", text: t("branch_hr_test_unmapped") };
            } else if (d.reason === "hr_unconfigured") {
                res = { tone: "error", text: t("branch_hr_test_unconfigured") };
            } else if (d.reason === "hr_unreachable") {
                res = { tone: "error", text: t("branch_hr_test_unreachable") };
            } else {
                res = { tone: "error", text: d.reason || t("error_generic") };
            }
            setHrTests((prev) => ({ ...prev, [b.id]: { loading: false, ...res } }));
        } catch (e) {
            setHrTests((prev) => ({ ...prev, [b.id]: { loading: false, tone: "error", text: apiErrorText(e, t("error_generic")) } }));
        }
    };

    const fetchBranches = useCallback(async () => {
        const r = await api.get("/branches", { params: { include_inactive: true } });
        setBranches(r.data);
    }, []);

    // Never blocks the page: without it the table still works, the select just stays empty.
    const fetchOpsManagers = useCallback(async () => {
        if (!isSystemAdmin) return;
        try {
            const [d, c] = await Promise.all([
                api.get("/branches/ops-managers"),
                api.get("/branches/ops-manager-candidates"),
            ]);
            setOpsManagers(Object.fromEntries((d.data || []).map((x) => [x.branch_id, x])));
            setOpsCandidates(c.data || []);
        } catch (e) {
            setOpsManagers({});
            setOpsCandidates([]);
        }
    }, [isSystemAdmin]);

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([fetchBranches(), fetchOpsManagers()]);
            } catch (e) {
                toast.error(t("error_generic"));
            } finally {
                setLoading(false);
            }
        })();
    }, [fetchBranches, fetchOpsManagers, t]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        heldPos.current = "";
        setOpen(true);
    };
    const openEdit = (b) => {
        setEditing(b);
        heldPos.current = "";
        setForm({
            name_ar: b.name_ar || "",
            name_en: b.name_en || "",
            code: b.code || "",
            pos_branch_id: b.pos_branch_id != null ? String(b.pos_branch_id) : "",
            finance_only: !!b.finance_only,
            hr_branch_id: b.hr_branch_id || "",
            ops_manager_user_id: opsManagers[b.id]?.user_id || "",
        });
        setOpen(true);
    };

    // The select is for a system admin on an existing branch. A new designee can be chosen on an
    // active, operational branch only (the server refuses the others); a branch that HAS one always
    // shows it, so it can be cleared, even while «مالي فقط» is switched on (the server wants the
    // designation cleared first; save() clears it before the branch is saved). Options: every
    // account the server would accept, plus the current designee if they no longer qualify.
    const currentOps = editing ? opsManagers[editing.id] : null;
    const canChooseOps = isSystemAdmin && !!editing && !!editing.is_active && !form.finance_only;
    const showOpsManager = canChooseOps || (isSystemAdmin && !!currentOps);
    const opsOptions = (() => {
        const list = canChooseOps ? [...opsCandidates] : [];
        if (currentOps && !list.some((u) => u.id === currentOps.user_id)) list.unshift({ id: currentOps.user_id, name: currentOps.user_name });
        return list;
    })();

    const save = async (confirmed = false) => {
        if (!form.name_ar.trim()) return toast.error(t("branch_name_ar"));
        // pos_branch_id: allow empty (send null on edit to clear, omit on create).
        // If set, must parse to a positive integer.
        let posId = null;
        const s = String(form.pos_branch_id).trim();
        if (s !== "") {
            if (!/^\d+$/.test(s) || Number(s) <= 0) {
                return toast.error(t("branch_pos_id_invalid"));
            }
            posId = Number(s);
        }
        // Finance-only should not have a POS id — protect the user from a
        // conflicting save that Sales/Waste can't reconcile.
        if (form.finance_only && posId !== null) {
            return toast.error(t("branch_finance_only_no_pos"));
        }
        const hrId = form.hr_branch_id.trim();
        if (hrId && hrId !== (editing?.hr_branch_id || "") && !HR_ID_RE.test(hrId)) {
            return toast.error(t("branch_hr_id_invalid"));
        }
        // Clearing a POS id unlinks the branch from the nightly sync and the sales
        // views; clearing an HR id empties its employee list. Say so first.
        if (editing && !confirmed) {
            const lost = [];
            if (editing.pos_branch_id != null && posId === null) {
                lost.push(`${t("branch_pos_id")} #${editing.pos_branch_id} — ${t("branch_clear_pos_effect")}`);
            }
            if (editing.hr_branch_id && !hrId) {
                lost.push(`${t("branch_hr_id")} ${String(editing.hr_branch_id).slice(0, 8)}… — ${t("branch_clear_hr_effect")}`);
            }
            if (lost.length) {
                setClearing(lost);
                return;
            }
        }
        setClearing(null);

        setSaving(true);
        try {
            if (editing) {
                const payload = {
                    name_ar: form.name_ar.trim(),
                    name_en: form.name_en.trim() || null,
                    code: form.code.trim() || null,
                    pos_branch_id: posId,   // null clears
                    finance_only: !!form.finance_only,
                    hr_branch_id: form.hr_branch_id.trim() || null,   // null clears
                };
                // its own endpoint (system admin): only when the choice changed. A cleared
                // designation goes FIRST (a designated branch cannot turn finance-only); a new
                // one after the branch is saved.
                const before = opsManagers[editing.id]?.user_id || "";
                const opsChanged = showOpsManager && form.ops_manager_user_id !== before;
                const putOps = async () => {
                    try {
                        await api.put(`/branches/${editing.id}/ops-manager`, { user_id: form.ops_manager_user_id || null });
                        return true;
                    } catch (e) {
                        toast.error(apiErrorText(e, t("branch_ops_manager_saved_failed")));
                        fetchBranches();
                        fetchOpsManagers();
                        return false;
                    }
                };
                if (opsChanged && !form.ops_manager_user_id && !(await putOps())) return;
                await api.patch(`/branches/${editing.id}`, payload);
                if (opsChanged && form.ops_manager_user_id && !(await putOps())) return;
            } else {
                const payload = {
                    name_ar: form.name_ar.trim(),
                    name_en: form.name_en.trim() || null,
                    code: form.code.trim() || null,
                    finance_only: !!form.finance_only,
                };
                if (posId !== null) payload.pos_branch_id = posId;
                if (form.hr_branch_id.trim()) payload.hr_branch_id = form.hr_branch_id.trim();
                await api.post("/branches", payload);
            }
            toast.success(t("save_success"));
            setOpen(false);
            fetchBranches();
            fetchOpsManagers();
        } catch (e) {
            toast.error(apiErrorText(e, t("error_generic")));
            fetchOpsManagers();   // a designation cleared before a refused save is gone already
        } finally {
            setSaving(false);
        }
    };

    // Finance-only ON clears the POS id (the two never go together); OFF puts back
    // the id the form had, so a stray tap can no longer unlink the branch from POS.
    const setFinanceOnly = (v) => {
        if (v) {
            heldPos.current = String(form.pos_branch_id || "").trim();
            setForm((f) => ({ ...f, finance_only: true, pos_branch_id: "" }));
        } else {
            const back = heldPos.current;
            heldPos.current = "";
            setForm((f) => ({ ...f, finance_only: false,
                pos_branch_id: String(f.pos_branch_id || "").trim() ? f.pos_branch_id : back }));
        }
    };

    const setActive = async (b, isActive) => {
        try {
            await api.patch(`/branches/${b.id}`, { is_active: isActive });
            toast.success(t("action_success"));
            fetchBranches();
        } catch (e) {
            toast.error(apiErrorText(e, t("error_generic")));
        }
    };

    // Reactivating stays one tap; deactivating asks for the branch name first.
    const toggleActive = (b) => {
        if (!b.is_active) return setActive(b, true);
        setTypedName("");
        setDeactivating(b);
    };

    const nameMatches = (b, typed) => {
        const s = typed.trim();
        return !!s && (s === (b?.name_ar || "").trim() || s === (b?.name_en || "").trim());
    };

    const confirmDeactivate = async () => {
        const b = deactivating;
        if (!b || !nameMatches(b, typedName)) return;
        setDeactivating(null);
        await setActive(b, false);
    };

    return (
        <div data-testid={OPS.branches.page} className="space-y-5">
            <PageHeader
                icon={Building2}
                title={t("branches_title")}
                subtitle={t("branches_subtitle")}
                primaryAction={
                    <Button
                        data-testid={OPS.branches.add}
                        onClick={openCreate}
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        {t("add_branch")}
                    </Button>
                }
            />

            <div className="rounded-xl surface border border-[color:var(--border-soft)] overflow-hidden">
                {loading ? (
                    <InlineLoader label={t("loading")} />
                ) : (
                    <div className="overflow-x-auto">
                        <Table data-testid={OPS.branches.table}>
                            <TableHeader>
                                <TableRow className="bg-[color:rgba(224,222,217,0.55)] hover:bg-[color:rgba(224,222,217,0.55)]">
                                    <TableHead className="text-start">{t("branch_name_ar")}</TableHead>
                                    <TableHead className="text-start">{t("branch_name_en")}</TableHead>
                                    <TableHead className="text-start">{t("branch_code")}</TableHead>
                                    <TableHead className="text-start">{t("branch_pos_id")}</TableHead>
                                    <TableHead className="text-start">{t("branch_hr_id")}</TableHead>
                                    <TableHead className="text-start">{t("branch_flags")}</TableHead>
                                    <TableHead className="text-start">{t("status")}</TableHead>
                                    <TableHead className="text-end">{t("actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {branches.map((b) => (
                                    <TableRow key={b.id} data-testid={OPS.branches.row(b.id)} className="hover:bg-[color:rgba(186,217,235,0.20)]">
                                        <TableCell className="font-medium text-[color:var(--ink)]">
                                            <div>{b.name_ar}</div>
                                            {opsManagers[b.id] ? (
                                                <div
                                                    data-testid={OPS.branches.opsManager(b.id)}
                                                    title={t("branch_ops_manager_desc")}
                                                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-normal text-[color:var(--brand-brown)]"
                                                >
                                                    <UserCog className="h-3 w-3 shrink-0" />
                                                    <span>{t("branch_ops_manager")}:</span>
                                                    <span className="font-semibold">{opsManagers[b.id].user_name || "—"}</span>
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell className="text-[color:var(--ink-muted)]">
                                            <bdi dir="ltr">{b.name_en || "—"}</bdi>
                                        </TableCell>
                                        <TableCell className="text-[color:var(--ink-muted)]">{b.code || "—"}</TableCell>
                                        <TableCell className="text-[color:var(--ink-muted)] tnum">
                                            {b.pos_branch_id != null ? (
                                                <span data-testid={OPS.branches.posId(b.id)} className="inline-flex items-center gap-1">
                                                    <Hash className="h-3 w-3 opacity-60" />
                                                    <bdi dir="ltr">{b.pos_branch_id}</bdi>
                                                </span>
                                            ) : "—"}
                                        </TableCell>
                                        <TableCell className="text-[color:var(--ink-muted)] align-top">
                                            <div className="space-y-1.5">
                                                <div>
                                                    {b.hr_branch_id ? (
                                                        <span data-testid={OPS.branches.hrId(b.id)} className="inline-flex items-center gap-1" title={b.hr_branch_id}>
                                                            <Link2 className="h-3 w-3 opacity-60" />
                                                            <bdi dir="ltr" className="font-mono text-[11px]">{String(b.hr_branch_id).slice(0, 8)}…</bdi>
                                                        </span>
                                                    ) : (
                                                        <Badge
                                                            data-testid={OPS.branches.hrId(b.id)}
                                                            variant="secondary"
                                                            className="bg-[color:rgba(185,178,168,0.3)] text-[color:var(--ink-muted)] border-0 text-[10px]"
                                                        >
                                                            {t("branch_hr_unmapped")}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {isSuperAdmin ? (
                                                    <div>
                                                        <Button
                                                            data-testid={OPS.branches.hrTestBtn(b.id)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 gap-1 px-2 text-[11px]"
                                                            onClick={() => testHr(b)}
                                                            disabled={hrTests[b.id]?.loading}
                                                        >
                                                            {hrTests[b.id]?.loading ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <FlaskConical className="h-3 w-3" />
                                                            )}
                                                            {t("branch_hr_test")}
                                                        </Button>
                                                        {hrTests[b.id] && !hrTests[b.id].loading ? (
                                                            <HrTestResult r={hrTests[b.id]} bid={b.id} t={t} />
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {b.finance_only ? (
                                                <Badge
                                                    data-testid={OPS.branches.financeOnlyBadge(b.id)}
                                                    variant="secondary"
                                                    className="gap-1 bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0"
                                                >
                                                    <FileText className="h-3 w-3" />
                                                    {t("branch_finance_only")}
                                                </Badge>
                                            ) : (
                                                <span className="text-[color:var(--ink-muted)]">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="secondary"
                                                className={
                                                    b.is_active
                                                        ? "bg-[color:rgba(31,122,77,0.14)] text-[color:var(--success)] border-0"
                                                        : "bg-[color:rgba(185,178,168,0.3)] text-[color:var(--ink-muted)] border-0"
                                                }
                                            >
                                                {b.is_active ? t("active") : t("inactive")}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-end">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" aria-label="actions">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-44">
                                                    <DropdownMenuItem
                                                        data-testid={OPS.branches.rename(b.id)}
                                                        onClick={() => openEdit(b)}
                                                        className="cursor-pointer"
                                                    >
                                                        <Pencil className="h-4 w-4 me-2" />
                                                        {t("rename")}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        data-testid={OPS.branches.toggle(b.id)}
                                                        onClick={() => toggleActive(b)}
                                                        className="cursor-pointer"
                                                    >
                                                        <Power className="h-4 w-4 me-2" />
                                                        {b.is_active ? t("deactivate") : t("activate")}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto scrollbar-thin">
                    <DialogHeader>
                        <DialogTitle>{editing ? t("rename") : t("add_branch")}</DialogTitle>
                        <DialogDescription>{t("branches_subtitle")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>{t("branch_name_ar")}</Label>
                            <Input
                                data-testid={OPS.branches.form.nameAr}
                                value={form.name_ar}
                                onChange={(e) => setForm((f) => ({ ...f, name_ar: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("branch_name_en")}</Label>
                            <Input
                                data-testid={OPS.branches.form.nameEn}
                                value={form.name_en}
                                onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("branch_code")}</Label>
                            <Input
                                data-testid={OPS.branches.form.code}
                                value={form.code}
                                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="flex items-center gap-1.5">
                                <Hash className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />
                                {t("branch_pos_id")}
                                <span className="text-xs text-[color:var(--ink-muted)] font-normal">{t("branch_pos_id_hint")}</span>
                            </Label>
                            <Input
                                data-testid={OPS.branches.form.posId}
                                type="number"
                                min="1"
                                step="1"
                                value={form.pos_branch_id}
                                onChange={(e) => setForm((f) => ({ ...f, pos_branch_id: e.target.value }))}
                                dir="ltr"
                                className="text-start"
                                placeholder="—"
                                disabled={form.finance_only}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="flex items-center gap-1.5">
                                <Link2 className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />
                                {t("branch_hr_id")}
                                <span className="text-xs text-[color:var(--ink-muted)] font-normal">{t("branch_hr_id_hint")}</span>
                            </Label>
                            <Input
                                data-testid={OPS.branches.form.hrId}
                                value={form.hr_branch_id}
                                onChange={(e) => setForm((f) => ({ ...f, hr_branch_id: e.target.value }))}
                                dir="ltr"
                                className="text-start font-mono text-sm"
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            />
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-soft)] px-3 py-2.5">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-medium text-[color:var(--ink)]">{t("branch_finance_only")}</Label>
                                <p className="text-xs text-[color:var(--ink-muted)]">{t("branch_finance_only_desc")}</p>
                            </div>
                            <Switch
                                data-testid={OPS.branches.form.financeOnly}
                                checked={form.finance_only}
                                onCheckedChange={setFinanceOnly}
                            />
                        </div>
                        {showOpsManager ? (
                            <div className="space-y-1.5">
                                <Label className="flex items-center gap-1.5">
                                    <UserCog className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />
                                    <span>{t("branch_ops_manager")}</span>
                                    <span className="text-xs text-[color:var(--ink-muted)] font-normal">{t("branch_ops_manager_hint")}</span>
                                </Label>
                                <Select
                                    value={form.ops_manager_user_id || NO_OPS_MANAGER}
                                    onValueChange={(v) => setForm((f) => ({ ...f, ops_manager_user_id: v === NO_OPS_MANAGER ? "" : v }))}
                                >
                                    <SelectTrigger data-testid={OPS.branches.form.opsManager}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NO_OPS_MANAGER}>{t("branch_ops_manager_none")}</SelectItem>
                                        {opsOptions.map((u) => (
                                            <SelectItem key={u.id} value={u.id} data-testid={OPS.branches.form.opsManagerOption(u.id)}>
                                                {u.name || u.email || u.id}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-[color:var(--ink-muted)]">{t("branch_ops_manager_desc")}</p>
                            </div>
                        ) : null}
                        {form.finance_only && editing?.pos_branch_id != null ? (
                            <p data-testid={OPS.branches.form.posWarning}
                                className="flex items-start gap-1.5 rounded-lg bg-[color:var(--warning-soft)] px-3 py-2 text-xs text-[color:#B45309]">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span>{t("branch_finance_only_unlinks").replace("{n}", editing.pos_branch_id)}</span>
                            </p>
                        ) : null}
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                            {t("cancel")}
                        </Button>
                        <Button
                            data-testid={OPS.branches.form.save}
                            onClick={() => save()}
                            disabled={saving}
                            className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                        >
                            {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                            {t("save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Save that removes a POS / HR link */}
            <AlertDialog open={!!clearing} onOpenChange={(o) => !o && setClearing(null)}>
                <AlertDialogContent data-testid={OPS.branches.clearConfirm}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("branch_clear_title")}</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-1.5 text-sm text-[color:var(--ink-muted)]">
                                {(clearing || []).map((line) => <p key={line}>{line}</p>)}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={OPS.branches.clearConfirmSave} onClick={() => save(true)}
                            className="bg-[color:var(--danger)] hover:bg-[#9A1C14] text-white">
                            {t("branch_clear_save")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Deactivate: type the branch name */}
            <AlertDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
                <AlertDialogContent data-testid={OPS.branches.deactivate.dialog}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("deactivate")} — {deactivating?.name_ar}</AlertDialogTitle>
                        <AlertDialogDescription>{t("branch_deactivate_effect")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-1.5">
                        <Label>{t("branch_deactivate_type").replace("{name}", deactivating?.name_ar || "")}</Label>
                        <Input
                            data-testid={OPS.branches.deactivate.input}
                            value={typedName}
                            onChange={(e) => setTypedName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") confirmDeactivate(); }}
                            autoComplete="off"
                        />
                    </div>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <Button
                            data-testid={OPS.branches.deactivate.confirm}
                            onClick={confirmDeactivate}
                            disabled={!nameMatches(deactivating, typedName)}
                            className="bg-[color:var(--danger)] hover:bg-[#9A1C14] text-white"
                        >
                            {t("deactivate")}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function HrTestResult({ r, bid, t }) {
    const cls =
        r.tone === "success"
            ? "text-[color:var(--success)]"
            : r.tone === "warn"
                ? "text-[color:#b45309]"
                : "text-[color:#b91c1c]";
    const Icon = r.tone === "success" ? CheckCircle2 : r.tone === "warn" ? AlertTriangle : XCircle;
    return (
        <div data-testid={OPS.branches.hrTestResult(bid)} data-hr-tone={r.tone} className={`mt-1 text-[11px] leading-snug ${cls}`}>
            <span className="inline-flex items-center gap-1 font-semibold">
                <Icon className="h-3 w-3 shrink-0" />
                {r.text}
            </span>
            {r.hr ? (
                <div className="text-[10px] text-[color:var(--ink-muted)] font-mono" dir="ltr">
                    {t("branch_hr_test_using")} {r.hr}
                </div>
            ) : null}
        </div>
    );
}
